/**
 * Core Blockchain ICAN address encoding (22 bytes = 44 hex chars, no 0x prefix):
 * CID + checksum + BCAN (40 hex = 20-byte account body). Mirrors {@code CoreBlockchainIcanAddress}
 * in tokenizer-backend (go-core checksum rules). For Ping’s EVM-compatible layer, the BCAN matches the
 * account bytes used with Solidity contracts and prepared transactions on the Core node.
 */
/* eslint-env es2020 */

/** @param {string} s */
function stripOptional0x(s) {
  const t = String(s || '').trim();
  return t.startsWith('0x') || t.startsWith('0X') ? t.slice(2) : t;
}

/** @param {string} hex */
function hexToBytes(hex) {
  const h = hex.length % 2 === 0 ? hex : `0${hex}`;
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = parseInt(h.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/**
 * @param {Uint8Array} addressBody20
 * @param {number} networkPrefixByte 0–255
 */
function calculateChecksum(addressBody20, networkPrefixByte) {
  const concat = new Uint8Array(21);
  concat.set(addressBody20, 0);
  concat[20] = networkPrefixByte;

  let addrString = '';
  for (let i = 0; i < 21; i += 1) {
    addrString += concat[i].toString(16).toUpperCase().padStart(2, '0');
  }
  addrString += '00';

  let mods = '';
  for (let j = 0; j < addrString.length; j += 1) {
    const c = addrString[j];
    const i = c.charCodeAt(0);
    if (i > 64 && i < 91) {
      mods += String(i - 55);
    } else {
      mods += c;
    }
  }

  const bigVal = BigInt(mods);
  const remainder = bigVal % 97n;
  const checksum = 98n - remainder;
  const resInt = Number(checksum);
  if (resInt < 10) {
    return `0${resInt}`;
  }
  return String(resInt);
}

/**
 * Matches backend {@code ResolveNetworkPrefixHex}: chain id 1 → cb, 3 → ab, else ce.
 * Override with REACT_APP_CORE_ICAN_NETWORK_PREFIX (two hex chars) when set.
 * @returns {string} two lowercase hex characters (e.g. "ab")
 */
export function resolveNetworkPrefixHex() {
  const explicit = (process.env.REACT_APP_CORE_ICAN_NETWORK_PREFIX || '').trim().toLowerCase();
  if (explicit.length === 2 && /^[0-9a-f]{2}$/.test(explicit)) {
    return explicit;
  }
  const id = String(process.env.REACT_APP_CHAIN_ID || '3').trim();
  if (id === '1') return 'cb';
  if (id === '3') return 'ab';
  return 'ce';
}

/**
 * @param {string} ethereumAddressWith0x e.g. 0x + 40 hex from ethers
 * @returns {string} 44 lowercase hex chars (ICAN)
 */
export function encodeIcanFromEthereumAddress(ethereumAddressWith0x) {
  const h = stripOptional0x(ethereumAddressWith0x);
  if (h.length !== 40 || !/^[0-9a-fA-F]+$/.test(h)) {
    throw new Error('Expected 0x + 40 hex character Ethereum address');
  }
  const body20 = hexToBytes(h);
  const prefixHex = resolveNetworkPrefixHex();
  const prefixByte = hexToBytes(prefixHex)[0];
  const checksumStr = calculateChecksum(body20, prefixByte);
  if (checksumStr.length < 1 || checksumStr.length > 2) {
    throw new Error('Internal error: ICAN checksum');
  }
  // Matches C# HexToBytes(checksumStr): two decimal digits rendered then parsed as hex pairs.
  const checksumByte = parseInt(checksumStr, 16);
  const full = new Uint8Array(22);
  full[0] = prefixByte;
  full[1] = checksumByte;
  full.set(body20, 2);
  let out = '';
  for (let i = 0; i < 22; i += 1) {
    out += full[i].toString(16).padStart(2, '0');
  }
  return out.toLowerCase();
}

/**
 * Converts Core ICAN (44 hex) or bare 40-hex to <c>0x</c> + 20-byte form for Solidity ABI / Nethereum.
 * Mirrors backend {@code CoreBlockchainIcanAddress.ToEthereumInteropHex} (BCAN = last 20 bytes of ICAN).
 * @param {string} address
 * @returns {string}
 */
export function toEthereumInteropHex(address) {
  const t = String(address || '').trim();
  if (!t) return t;
  const h = stripOptional0x(t);
  if (h.length === 40 && /^[0-9a-fA-F]+$/i.test(h)) {
    return `0x${h.toLowerCase()}`;
  }
  if (h.length === 44 && /^[0-9a-fA-F]+$/i.test(h)) {
    return `0x${h.slice(4).toLowerCase()}`;
  }
  if (t.startsWith('0x') || t.startsWith('0X')) {
    return `0x${h.toLowerCase()}`;
  }
  return `0x${h.toLowerCase()}`;
}

/** @param {string} v */
export function isValidEthereumWalletAddress(v) {
  const h = stripOptional0x(v);
  return h.length === 40 && /^[0-9a-fA-F]+$/.test(h);
}

/** @param {string} v */
export function isValidCoreIcanAddress(v) {
  const h = stripOptional0x(v);
  return h.length === 44 && /^[0-9a-fA-F]+$/.test(h);
}

/** Ethereum 0x+40 or Core ICAN 44 hex (optional 0x on ICAN ignored for length check). */
export function isValidBlockchainWalletAddress(v) {
  const t = String(v || '').trim();
  if (!t) return false;
  return isValidEthereumWalletAddress(t) || isValidCoreIcanAddress(t);
}

/**
 * For UI on Core: prefer CIP-100 ICAN (44 lowercase hex, no 0x) when the API stored interoperable <c>0x</c>+20 bytes
 * (same account body as Solidity). Already-ICAN values are normalized; unknown strings are returned unchanged.
 * @param {string} address
 * @returns {string}
 */
export function toCoreDisplayAddress(address) {
  const t = String(address || '').trim();
  if (!t) return '';
  if (isValidCoreIcanAddress(t)) {
    return stripOptional0x(t).toLowerCase();
  }
  if (isValidEthereumWalletAddress(t)) {
    try {
      return encodeIcanFromEthereumAddress(toEthereumInteropHex(t));
    } catch {
      return t;
    }
  }
  return t;
}
